import { useEffect, useState } from 'react';

import { InfiniteScroll, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import AssetsGridHeader from 'components/AssetsGrid/components/AssetsGridHeader';
import WithArrow from 'components/WithArrow';
import WithResize from 'components/WithResize';
import GridText from 'components/text/GridText';

import useGridTablesResizeObserver from 'hooks/useGridTablesResizeObserver';

import useTranslation from './hooks/useTranslation';

import AssetsGridRow from './components/AssetsGridRow';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const AssetsGrid: FunctionComponent<Props> = ({
    className = '',
    onNext,
    rows = [],
    testId = TEST_ID
}) => {
    const { ASSET_TYPE, LAST_ACTIVITY, LAST_EDITED, LOADING, NAME, NO_ASSETS, NO_MORE_ASSETS } =
        useTranslation();

    const { containerRef, handleResize } = useGridTablesResizeObserver('--assets-grid-row-columns');
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (!initialized) {
            const hasNext = typeof onNext === 'function';

            setHasNextPage(hasNext);

            if (hasNext) {
                setInitialized(true);
            }
        }
    }, [initialized, onNext]);

    const fetchNext = async () => {
        if (!hasNextPage || !onNext) {
            return;
        }

        const { done } = await onNext();

        setHasNextPage(!done);
    };

    const endingMessage = initialized && rows.length === 0 ? NO_ASSETS : NO_MORE_ASSETS;

    return (
        <VerticalContainer
            className={`${className} ${styles.assetsGrid}`}
            ref={containerRef}
            testId={testId}>
            <AssetsGridHeader>
                <WithResize onResize={handleResize}>
                    <WithArrow className={styles.withArrow}>
                        <GridText text={NAME} />
                    </WithArrow>
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={ASSET_TYPE} />
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={LAST_EDITED} />
                </WithResize>

                <GridText text={LAST_ACTIVITY} />
            </AssetsGridHeader>

            <InfiniteScroll
                endingMessage={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={endingMessage} />
                    </Wrapper>
                }
                loader={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={LOADING} />
                    </Wrapper>
                }
                className={styles.infiniteScroll}
                fetchNextPage={fetchNext}
                hasNextPage={hasNextPage}>
                {rows.map(
                    ({ assetType, faaRegistrationNumber, id, name, serialNumber, updatedAt }) => (
                        <AssetsGridRow
                            assetType={assetType}
                            faaRegistrationNumber={faaRegistrationNumber}
                            id={id}
                            key={id}
                            lastEdited={updatedAt}
                            name={name}
                            serialNumber={serialNumber}
                        />
                    )
                )}
            </InfiniteScroll>
        </VerticalContainer>
    );
};

export default AssetsGrid;
